import {
  fetchSignInMethodsForEmail,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithRedirect,
  signInWithPopup,
  signInWithCustomToken,
  getAdditionalUserInfo,
  getRedirectResult,
  User,
} from "firebase/auth";
import { amplitude, trackFbq } from "./third-party";
import { app } from "./firebaseApp";
import { useEffect, useState } from "react";
import { useMainStore } from "../common/store";

export const auth = getAuth(app);

const signIn =
  import.meta.env.VITE_SENTRY_ENVIRONMENT === "staging"
    ? signInWithPopup
    : signInWithRedirect;

let isPasswordSignUp: boolean | undefined = undefined;

export async function signInWithGoogle() {
  amplitude.logEvent(`Web:Client:Login:Attempt`, { type: "google" });
  const googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({ prompt: "select_account" });
  await signIn(auth, googleProvider);
}

export async function signInWithApple() {
  amplitude.logEvent(`Web:Client:Login:Attempt`, { type: "apple" });
  const appleProvider = new OAuthProvider("apple.com");
  await signIn(auth, appleProvider);
}

export async function signInWithEmail(email: string, password: string) {
  amplitude.logEvent("Web:Client:Login:Attempt", { type: "email" });
  isPasswordSignUp = false;
  const credentials = await signInWithEmailAndPassword(auth, email, password);

  return credentials;
}

export async function signUpWithEmail(email: string, password: string) {
  amplitude.logEvent("Web:Client:Register:Attempt", { type: "email" });
  isPasswordSignUp = true;
  const credentials = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );

  return credentials;
}

export async function resetPassword(email: string) {
  await sendPasswordResetEmail(auth, email);

  amplitude.logEvent("Web:Client:Login:PasswordReset");
}

export function useSignInWithSearchParamsToken() {
  const authToken = new URLSearchParams(location.search).get("authToken");
  const [isLoading, setIsLoading] = useState(!!authToken);

  useEffect(() => {
    async function signIn() {
      if (!authToken) return;
      try {
        await signInWithCustomToken(auth, authToken);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }
    void signIn();
  }, [authToken]);

  return isLoading;
}

export function useLoggedInSync() {
  const setIsLoggedIn = useMainStore((state) => state.setIsLoggedIn);
  const [initialLoading, setInitialLoading] = useState(true);
  const isAuthTokenLoading = useSignInWithSearchParamsToken();

  useEffect(() => {
    async function onStateChange(user: User | null) {
      if (user) {
        if (user.providerData[0].providerId !== "password") {
          let newUser: boolean | undefined = false;
          const credentials = await getRedirectResult(auth);
          if (credentials) {
            // Was a login via redirect, checking if it was the first time
            const userAdditionalInfos = getAdditionalUserInfo(credentials);
            newUser = userAdditionalInfos?.isNewUser;
          }
          // Else, it was a login thanks to the stored session, so not a new user
          if (newUser) {
            trackFbq("SignUp");
          }
          amplitude.logEvent("Web:Client:Auth:Success", {
            isNewUser: newUser,
            provider: user.providerData[0].providerId,
          });
        } else {
          // if it's a password login, we check if the signIn/Register was called, if it is not it's from the session and not a new user
          if (isPasswordSignUp) {
            trackFbq("SignUp");
          }
          amplitude.logEvent("Web:Client:Auth:Success", {
            isNewUser: isPasswordSignUp ?? false,
            provider: "password",
          });
          isPasswordSignUp = undefined;
        }
        amplitude.setUserId(user.uid);
      }
      setIsLoggedIn(!!user);
      setInitialLoading(false);
    }
    return auth.onAuthStateChanged((user) => void onStateChange(user));
  }, [setIsLoggedIn, setInitialLoading]);

  return initialLoading || isAuthTokenLoading;
}

export async function logout() {
  await signOut(auth);
}

export async function getUserExists(email: string): Promise<boolean> {
  const signInMethods = await fetchSignInMethodsForEmail(auth, email);
  return signInMethods.includes("password");
}
